import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getSecureItem, setSecureItem, removeSecureItem } from '../utils/secureStorage';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getSecureItem('token'));

  // Setup axios interceptor to add token to all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          // Token is invalid, clear it
          removeSecureItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]); // token is the only dependency needed - axios and API are stable

  const register = async (email, password, screenname) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      screenname
    });
    const { access_token, user: userData } = response.data;
    setSecureItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    setSecureItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    removeSecureItem('token');
    setToken(null);
    setUser(null);
  };

  const updateScreenname = async (newScreenname) => {
    const response = await axios.patch(`${API}/auth/screenname`, {
      screenname: newScreenname
    });
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    updateScreenname,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
